import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Users, Globe, FileText, Building, CreditCard, Truck, Shield, ChevronDown, ChevronUp, ArrowRight, Loader2, X, File, Search, LayoutGrid, Landmark } from 'lucide-react';
// Enhanced analytics tracking function
const trackEvent = (eventName: string, eventData: any) => {
  console.log(`Analytics Event: ${eventName}`, eventData);
  // In a real app, this would call your analytics service
  // Example: analytics.track(eventName, eventData);
  // Save to session storage for demo purposes
  const events = JSON.parse(sessionStorage.getItem('analyticsEvents') || '[]');
  events.push({
    eventName,
    eventData,
    timestamp: new Date().toISOString()
  });
  sessionStorage.setItem('analyticsEvents', JSON.stringify(events));
};
export const ServiceCategories = () => {
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showAllCategoriesModal, setShowAllCategoriesModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [viewType, setViewType] = useState<'categories' | 'agencies'>('categories');
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const navigate = useNavigate();
  // Load last viewed category from localStorage on component mount
  useEffect(() => {
    const lastViewedCategory = localStorage.getItem('lastViewedCategory');
    if (lastViewedCategory) {
      console.log(`User previously viewed: ${lastViewedCategory}`);
    }
  }, []);
  const toggleCategory = (id: number) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };
  // Count total services in a category
  const countServicesInCategory = (category: any) => {
    if (!category.subcategories) return 0;
    return category.subcategories.reduce((total: number, subcategory: any) => {
      return total + (subcategory.services?.length || 0);
    }, 0);
  };
  // Enhanced handler for "View all" link clicks
  const handleViewAllClick = (category: any, e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default link behavior
    // Track the event
    trackEvent('category_view_modal', {
      category: category.title,
      serviceCount: countServicesInCategory(category),
      timestamp: new Date().toISOString()
    });
    // Store last viewed category in localStorage
    localStorage.setItem('lastViewedCategory', category.title);
    // Set the selected category and show the modal
    setSelectedCategory(category);
    setShowModal(true);
  };
  // Close the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCategory(null);
  };
  // Navigate to a specific service
  const navigateToService = (serviceTitle: string, serviceLink: string) => {
    trackEvent('service_selected', {
      service: serviceTitle,
      link: serviceLink,
      category: selectedCategory?.title,
      timestamp: new Date().toISOString()
    });
    setShowModal(false);
    navigate(serviceLink);
  };
  // Toggle between categories and agencies
  const toggleViewType = (type: 'categories' | 'agencies') => {
    setViewType(type);
    setExpandedCategory(null);
    // Set a default filter when switching to 'agencies' view
    if (type === 'agencies') {
      setSelectedFilter('Ministry');
    } else {
      // Clear the filter when switching back to categories
      setSelectedFilter(null);
    }
    trackEvent('view_type_changed', {
      from: viewType,
      to: type,
      filterSet: type === 'agencies' ? 'Ministry' : null,
      timestamp: new Date().toISOString()
    });
  };
  // Toggle showing all items
  const toggleShowAll = () => {
    setShowAll(!showAll);
    trackEvent('toggle_show_all', {
      viewType,
      showingAll: !showAll,
      timestamp: new Date().toISOString()
    });
  };
  // Handle viewing all categories/agencies
  const handleViewAllCategoriesClick = () => {
    trackEvent('view_all_categories_modal', {
      viewType,
      timestamp: new Date().toISOString()
    });
    setShowAllCategoriesModal(true);
  };
  // Close the all categories modal
  const handleCloseAllCategoriesModal = () => {
    setShowAllCategoriesModal(false);
  };
  const categories = [{
    id: 1,
    title: 'Business Services',
    description: 'Register your business, apply for licenses and permits',
    icon: <Briefcase className="h-10 w-10 text-blue-600" data-id="element-139" />,
    link: '/services/business',
    subcategories: [{
      id: 101,
      title: 'Business Registration',
      services: [{
        id: 1001,
        title: 'Register a New Business',
        link: '/services/business/register-new'
      }, {
        id: 1002,
        title: 'Register a Branch',
        link: '/services/business/register-branch'
      }, {
        id: 1003,
        title: 'Business Name Registration',
        link: '/services/business/name-registration'
      }]
    }, {
      id: 102,
      title: 'Business Licenses',
      services: [{
        id: 1004,
        title: 'Trading License',
        link: '/services/business/trading-license'
      }, {
        id: 1005,
        title: 'Investment Certificate',
        link: '/services/business/investment-certificate'
      }, {
        id: 1006,
        title: 'Import/Export License',
        link: '/services/business/import-export-license'
      }]
    }]
  }, {
    id: 2,
    title: 'Civil Registry',
    description: 'National ID, birth certificates, marriage registration',
    icon: <Users className="h-10 w-10 text-blue-600" data-id="element-140" />,
    link: '/services/citizens',
    subcategories: [{
      id: 201,
      title: 'Personal Documents',
      services: [{
        id: 2001,
        title: 'National ID Application',
        link: '/services/citizens/national-id'
      }, {
        id: 2002,
        title: 'Birth Certificate',
        link: '/services/citizens/birth-certificate'
      }, {
        id: 2003,
        title: 'Marriage Certificate',
        link: '/services/citizens/marriage-certificate'
      }]
    }, {
      id: 202,
      title: 'Family Services',
      services: [{
        id: 2004,
        title: 'Child Registration',
        link: '/services/citizens/child-registration'
      }, {
        id: 2005,
        title: 'Family Book',
        link: '/services/citizens/family-book'
      }]
    }]
  }, {
    id: 3,
    title: 'Immigration & Residency',
    description: 'Passport applications, visa services, residence permits',
    icon: <Globe className="h-10 w-10 text-blue-600" data-id="element-141" />,
    link: '/services/immigration',
    subcategories: [{
      id: 301,
      title: 'Travel Documents',
      services: [{
        id: 3001,
        title: 'Passport Application',
        link: '/services/immigration/passport'
      }, {
        id: 3002,
        title: 'Emergency Travel Document',
        link: '/services/immigration/emergency-travel'
      }]
    }, {
      id: 302,
      title: 'Visa & Permits',
      services: [{
        id: 3003,
        title: 'Tourist Visa',
        link: '/services/immigration/tourist-visa'
      }, {
        id: 3004,
        title: 'Work Permit',
        link: '/services/immigration/work-permit'
      }, {
        id: 3005,
        title: 'Student Visa',
        link: '/services/immigration/student-visa'
      }, {
        id: 3006,
        title: 'Residence Permit',
        link: '/services/immigration/residence-permit'
      }]
    }]
  }, {
    id: 4,
    title: 'Document Services',
    description: 'Request official documents and certificates',
    icon: <FileText className="h-10 w-10 text-blue-600" data-id="element-142" />,
    link: '/services/documents',
    subcategories: [{
      id: 401,
      title: 'Certificates',
      services: [{
        id: 4001,
        title: 'Good Conduct Certificate',
        link: '/services/documents/good-conduct'
      }, {
        id: 4002,
        title: 'Academic Certificates',
        link: '/services/documents/academic-certificates'
      }]
    }, {
      id: 402,
      title: 'Legal Documents',
      services: [{
        id: 4003,
        title: 'Power of Attorney',
        link: '/services/documents/power-of-attorney'
      }, {
        id: 4004,
        title: 'Affidavit',
        link: '/services/documents/affidavit'
      }]
    }]
  }, {
    id: 5,
    title: 'Land & Property',
    description: 'Land registration, transfer, and property management',
    icon: <Building className="h-10 w-10 text-blue-600" data-id="element-143" />,
    link: '/services/property',
    subcategories: [{
      id: 501,
      title: 'Land Services',
      services: [{
        id: 5001,
        title: 'Land Registration',
        link: '/services/property/land-registration'
      }, {
        id: 5002,
        title: 'Land Transfer',
        link: '/services/property/land-transfer'
      }, {
        id: 5003,
        title: 'Land Title Search',
        link: '/services/property/land-title-search'
      }]
    }, {
      id: 502,
      title: 'Construction',
      services: [{
        id: 5004,
        title: 'Building Permit',
        link: '/services/property/building-permit'
      }, {
        id: 5005,
        title: 'Occupancy Certificate',
        link: '/services/property/occupancy-certificate'
      }]
    }]
  }, {
    id: 6,
    title: 'Taxes & Revenue',
    description: 'Pay taxes, fees, and other government charges',
    icon: <CreditCard className="h-10 w-10 text-blue-600" data-id="element-144" />,
    link: '/services/payments',
    subcategories: [{
      id: 601,
      title: 'Tax Payments',
      services: [{
        id: 6001,
        title: 'Income Tax',
        link: '/services/payments/income-tax'
      }, {
        id: 6002,
        title: 'Property Tax',
        link: '/services/payments/property-tax'
      }, {
        id: 6003,
        title: 'Value Added Tax',
        link: '/services/payments/vat'
      }]
    }, {
      id: 602,
      title: 'Utility Payments',
      services: [{
        id: 6004,
        title: 'Water Bill',
        link: '/services/payments/water-bill'
      }, {
        id: 6005,
        title: 'Electricity Bill',
        link: '/services/payments/electricity-bill'
      }]
    }]
  }, {
    id: 7,
    title: 'Transport',
    description: 'Vehicle registration, driving licenses, transport permits',
    icon: <Truck className="h-10 w-10 text-blue-600" data-id="element-145" />,
    link: '/services/transport',
    subcategories: [{
      id: 701,
      title: 'Driver Services',
      services: [{
        id: 7001,
        title: "Driver's License",
        link: '/services/transport/drivers-license'
      }, {
        id: 7002,
        title: 'License Renewal',
        link: '/services/transport/license-renewal'
      }]
    }, {
      id: 702,
      title: 'Vehicle Services',
      services: [{
        id: 7003,
        title: 'Vehicle Registration',
        link: '/services/transport/vehicle-registration'
      }, {
        id: 7004,
        title: 'Vehicle Inspection',
        link: '/services/transport/vehicle-inspection'
      }, {
        id: 7005,
        title: 'Vehicle Transfer',
        link: '/services/transport/vehicle-transfer'
      }]
    }]
  }, {
    id: 8,
    title: 'Security',
    description: 'Police clearance, security registrations and permits',
    icon: <Shield className="h-10 w-10 text-blue-600" data-id="element-146" />,
    link: '/services/security',
    subcategories: [{
      id: 801,
      title: 'Clearances',
      services: [{
        id: 8001,
        title: 'Police Clearance',
        link: '/services/security/police-clearance'
      }, {
        id: 8002,
        title: 'Security Vetting',
        link: '/services/security/security-vetting'
      }]
    }, {
      id: 802,
      title: 'Permits',
      services: [{
        id: 8003,
        title: 'Firearms License',
        link: '/services/security/firearms-license'
      }, {
        id: 8004,
        title: 'Security Company Registration',
        link: '/services/security/security-company'
      }]
    }]
  }];
  const agencies = [{
    id: 101,
    title: 'Ministry of Interior',
    description: 'National security, immigration, and civil registration',
    icon: <Shield className="h-10 w-10 text-blue-600" data-id="element-147" />,
    link: '/agencies/interior',
    subcategories: [{
      id: 1001,
      title: 'National Security',
      services: [{
        id: 10001,
        title: 'Police Clearance Certificate',
        link: '/services/security/police-clearance'
      }, {
        id: 10002,
        title: 'Security Vetting',
        link: '/services/security/security-vetting'
      }]
    }, {
      id: 1002,
      title: 'Immigration',
      services: [{
        id: 10003,
        title: 'Passport Application',
        link: '/services/immigration/passport'
      }, {
        id: 10004,
        title: 'Visa Processing',
        link: '/services/immigration/visa'
      }]
    }]
  }, {
    id: 102,
    title: 'Ministry of Finance',
    description: 'Taxation, revenue collection, and financial regulations',
    icon: <CreditCard className="h-10 w-10 text-blue-600" data-id="element-148" />,
    link: '/agencies/finance',
    subcategories: [{
      id: 1003,
      title: 'Taxation',
      services: [{
        id: 10005,
        title: 'Income Tax Filing',
        link: '/services/payments/income-tax'
      }, {
        id: 10006,
        title: 'VAT Registration',
        link: '/services/payments/vat'
      }]
    }, {
      id: 1004,
      title: 'Revenue',
      services: [{
        id: 10007,
        title: 'Tax Clearance Certificate',
        link: '/services/payments/tax-clearance'
      }, {
        id: 10008,
        title: 'Import Duty Payment',
        link: '/services/payments/import-duty'
      }]
    }]
  }, {
    id: 103,
    title: 'Ministry of Transport',
    description: 'Transportation, vehicle registration, and driver licensing',
    icon: <Truck className="h-10 w-10 text-blue-600" data-id="element-149" />,
    link: '/agencies/transport',
    subcategories: [{
      id: 1005,
      title: 'Driver Licensing',
      services: [{
        id: 10009,
        title: "Driver's License Application",
        link: '/services/transport/drivers-license'
      }, {
        id: 10010,
        title: 'License Renewal',
        link: '/services/transport/license-renewal'
      }]
    }, {
      id: 1006,
      title: 'Vehicle Registration',
      services: [{
        id: 10011,
        title: 'New Vehicle Registration',
        link: '/services/transport/vehicle-registration'
      }, {
        id: 10012,
        title: 'Vehicle Transfer',
        link: '/services/transport/vehicle-transfer'
      }]
    }]
  }, {
    id: 104,
    title: 'Ministry of Land',
    description: 'Land registration, property management, and urban planning',
    icon: <Building className="h-10 w-10 text-blue-600" data-id="element-150" />,
    link: '/agencies/land',
    subcategories: [{
      id: 1007,
      title: 'Land Registration',
      services: [{
        id: 10013,
        title: 'Land Title Registration',
        link: '/services/property/land-registration'
      }, {
        id: 10014,
        title: 'Land Transfer',
        link: '/services/property/land-transfer'
      }]
    }, {
      id: 1008,
      title: 'Building Permits',
      services: [{
        id: 10015,
        title: 'Construction Permit',
        link: '/services/property/building-permit'
      }, {
        id: 10016,
        title: 'Occupancy Certificate',
        link: '/services/property/occupancy-certificate'
      }]
    }]
  }, {
    id: 105,
    title: 'Ministry of Trade',
    description: 'Business registration, trade licenses, and commerce',
    icon: <Briefcase className="h-10 w-10 text-blue-600" data-id="element-151" />,
    link: '/agencies/trade',
    subcategories: [{
      id: 1009,
      title: 'Business Registration',
      services: [{
        id: 10017,
        title: 'New Business Registration',
        link: '/services/business/register-new'
      }, {
        id: 10018,
        title: 'Business Name Registration',
        link: '/services/business/name-registration'
      }]
    }, {
      id: 1010,
      title: 'Trade Licenses',
      services: [{
        id: 10019,
        title: 'Trading License',
        link: '/services/business/trading-license'
      }, {
        id: 10020,
        title: 'Import/Export License',
        link: '/services/business/import-export-license'
      }]
    }]
  }, {
    id: 106,
    title: 'Ministry of Health',
    description: 'Healthcare services, medical certifications, and regulations',
    icon: <Users className="h-10 w-10 text-blue-600" data-id="element-152" />,
    link: '/agencies/health',
    subcategories: [{
      id: 1011,
      title: 'Medical Certifications',
      services: [{
        id: 10021,
        title: 'Medical Certificate',
        link: '/services/health/medical-certificate'
      }, {
        id: 10022,
        title: 'Health Insurance Registration',
        link: '/services/health/insurance'
      }]
    }, {
      id: 1012,
      title: 'Healthcare Facilities',
      services: [{
        id: 10023,
        title: 'Hospital Registration',
        link: '/services/health/hospital-registration'
      }, {
        id: 10024,
        title: 'Pharmacy License',
        link: '/services/health/pharmacy-license'
      }]
    }]
  }, {
    id: 107,
    title: 'Ministry of Education',
    description: 'Educational services, school registrations, and certifications',
    icon: <FileText className="h-10 w-10 text-blue-600" data-id="element-153" />,
    link: '/agencies/education',
    subcategories: [{
      id: 1013,
      title: 'Academic Certifications',
      services: [{
        id: 10025,
        title: 'Certificate Verification',
        link: '/services/education/certificate-verification'
      }, {
        id: 10026,
        title: 'Transcript Request',
        link: '/services/education/transcript'
      }]
    }, {
      id: 1014,
      title: 'School Registration',
      services: [{
        id: 10027,
        title: 'School Registration',
        link: '/services/education/school-registration'
      }, {
        id: 10028,
        title: 'Student Enrollment',
        link: '/services/education/student-enrollment'
      }]
    }]
  }, {
    id: 108,
    title: 'Ministry of Justice',
    description: 'Legal services, court documents, and justice administration',
    icon: <Globe className="h-10 w-10 text-blue-600" data-id="element-154" />,
    link: '/agencies/justice',
    subcategories: [{
      id: 1015,
      title: 'Legal Documents',
      services: [{
        id: 10029,
        title: 'Power of Attorney',
        link: '/services/documents/power-of-attorney'
      }, {
        id: 10030,
        title: 'Affidavit',
        link: '/services/documents/affidavit'
      }]
    }, {
      id: 1016,
      title: 'Court Services',
      services: [{
        id: 10031,
        title: 'Court Case Filing',
        link: '/services/justice/case-filing'
      }, {
        id: 10032,
        title: 'Court Records Request',
        link: '/services/justice/records-request'
      }]
    }]
  }];
  // Determine which data to display based on viewType
  const displayData = viewType === 'categories' ? categories : agencies;
  // For mobile: 2 items (1 column × 2 rows)
  // For tablets: 4 items (2 columns × 2 rows)
  // For desktop: 8 items (4 columns × 2 rows)
  const getDisplayItems = () => {
    // Use a reasonable number of items that works for all screen sizes
    return showAll ? displayData : displayData.slice(0, 8);
  };
  const displayItems = getDisplayItems();
  return <section className="py-16 bg-white" data-id="element-155">
      <div className="container mx-auto px-4" data-id="element-156">
        <div className="text-center mb-16" data-id="element-157">
          <div className="relative mb-10" data-id="element-158">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-159">
              <div className="h-0.5 w-32 bg-blue-100" data-id="element-160"></div>
            </div>
            <h2 className="relative inline-block px-6 py-2 bg-white text-3xl font-bold text-gray-800" data-id="element-161">
              Government Services
            </h2>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg mb-10" data-id="element-162">
            Access a wide range of government services online, designed to make
            your interaction with government agencies faster and more
            convenient.
          </p>
          {/* Card-style toggle between categories and agencies */}
          <div className="flex flex-col items-center justify-center space-y-8" data-id="element-163">
            <div className="flex justify-center gap-4 w-full max-w-2xl mx-auto" data-id="element-164">
              <div onClick={() => toggleViewType('categories')} className={`flex flex-col items-center justify-center p-6 rounded-lg cursor-pointer transition-all duration-300 w-full max-w-xs
                  ${viewType === 'categories' ? 'bg-white border-2 border-blue-600 shadow-md transform scale-105' : 'bg-gray-50 hover:bg-blue-50 hover:shadow-sm'}`} data-id="element-165">
                <div className="p-3 bg-blue-100 rounded-full mb-3" data-id="element-166">
                  <LayoutGrid className="h-8 w-8 text-blue-600" data-id="element-167" />
                </div>
                <h3 className="text-lg font-semibold mb-1" data-id="element-168">
                  Browse by Category
                </h3>
                <p className="text-sm text-gray-600" data-id="element-169">
                  Find services organized by type
                </p>
              </div>
              <div onClick={() => toggleViewType('agencies')} className={`flex flex-col items-center justify-center p-6 rounded-lg cursor-pointer transition-all duration-300 w-full max-w-xs
                  ${viewType === 'agencies' ? 'bg-white border-2 border-blue-600 shadow-md transform scale-105' : 'bg-gray-50 hover:bg-blue-50 hover:shadow-sm'}`} data-id="element-170">
                <div className="p-3 bg-blue-100 rounded-full mb-3" data-id="element-171">
                  <Landmark className="h-8 w-8 text-blue-600" data-id="element-172" />
                </div>
                <h3 className="text-lg font-semibold mb-1" data-id="element-173">Browse by Agency</h3>
                <p className="text-sm text-gray-600" data-id="element-174">
                  Find services by government department
                </p>
              </div>
            </div>
            {/* View all categories/ministries link */}
            <a href="#" onClick={e => {
            e.preventDefault();
            handleViewAllCategoriesClick();
          }} className="flex items-center text-blue-600 font-medium hover:text-blue-800" data-id="element-175">
              View all {viewType === 'categories' ? 'categories' : 'ministries'}{' '}
              and services
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-176" />
            </a>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 grid-rows-2" data-id="element-177">
          {displayItems.map(item => {
          const serviceCount = countServicesInCategory(item);
          return <div key={item.id} className="flex flex-col bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow" data-id="element-178">
                <div className="flex flex-col items-center text-center p-6 cursor-pointer" onClick={() => toggleCategory(item.id)} data-id="element-179">
                  <div className="mb-4 p-3 bg-blue-50 rounded-full" data-id="element-180">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2" data-id="element-181">{item.title}</h3>
                  <p className="text-gray-600 mb-2" data-id="element-182">{item.description}</p>
                  <div className="mt-2 text-blue-600" data-id="element-183">
                    {expandedCategory === item.id ? <ChevronUp className="h-5 w-5" data-id="element-184" /> : <ChevronDown className="h-5 w-5" data-id="element-185" />}
                  </div>
                </div>
                {expandedCategory === item.id && <div className="px-6 pb-6 border-t pt-4" data-id="element-186">
                    {item.subcategories?.map(subcategory => <div key={subcategory.id} className="mb-4" data-id="element-187">
                        <h4 className="font-medium text-lg mb-2" data-id="element-188">
                          {subcategory.title}
                        </h4>
                        <ul className="space-y-1" data-id="element-189">
                          {subcategory.services.map(service => <li key={service.id} data-id="element-190">
                              <Link to={service.link} className="text-blue-600 hover:underline block py-1" data-id="element-191">
                                {service.title}
                              </Link>
                            </li>)}
                        </ul>
                      </div>)}
                    <button className="inline-flex items-center justify-center mt-4 px-4 py-2 bg-blue-50 text-blue-700 font-medium rounded-md hover:bg-blue-100 hover:text-blue-800 transition-all duration-300 w-full" onClick={e => handleViewAllClick(item, e)} data-id="element-192">
                      View all {item.title}
                      {serviceCount > 0 && <span className="ml-1 text-xs" data-id="element-193">({serviceCount})</span>}
                      <ArrowRight className="ml-2 h-4 w-4" data-id="element-194" />
                    </button>
                  </div>}
              </div>;
        })}
        </div>
        {/* View more/less button */}
        {displayData.length > 8 && <div className="text-center mt-10" data-id="element-195">
            <button onClick={toggleShowAll} className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors" data-id="element-196">
              {showAll ? <>
                  Show less{' '}
                  {viewType === 'categories' ? 'categories' : 'agencies'}
                  <ChevronUp className="ml-2 h-5 w-5" data-id="element-197" />
                </> : <>
                  View more{' '}
                  {viewType === 'categories' ? 'categories' : 'agencies'}
                  <ChevronDown className="ml-2 h-5 w-5" data-id="element-198" />
                </>}
            </button>
          </div>}
      </div>
      {/* Service Modal for a single category */}
      {showModal && selectedCategory && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-199">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-200">
            <div className="p-6" data-id="element-201">
              <div className="flex justify-between items-center mb-6" data-id="element-202">
                <h2 className="text-2xl font-bold" data-id="element-203">
                  All {selectedCategory.title}
                </h2>
                <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700" data-id="element-204">
                  <X className="h-6 w-6" data-id="element-205" />
                </button>
              </div>
              {selectedCategory.subcategories?.map(subcategory => <div key={subcategory.id} className="mb-8" data-id="element-206">
                  <h3 className="text-xl font-semibold mb-4" data-id="element-207">
                    {subcategory.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-208">
                    {subcategory.services.map(service => {
                // Determine appropriate icon based on service title
                let ServiceIcon = File;
                if (service.title.toLowerCase().includes('registration')) {
                  ServiceIcon = FileText;
                } else if (service.title.toLowerCase().includes('license')) {
                  ServiceIcon = CreditCard;
                } else if (service.title.toLowerCase().includes('certificate')) {
                  ServiceIcon = FileText;
                } else if (service.title.toLowerCase().includes('permit')) {
                  ServiceIcon = Shield;
                } else if (service.title.toLowerCase().includes('passport')) {
                  ServiceIcon = Globe;
                } else if (service.title.toLowerCase().includes('id') || service.title.toLowerCase().includes('identity')) {
                  ServiceIcon = Users;
                } else if (service.title.toLowerCase().includes('business')) {
                  ServiceIcon = Briefcase;
                } else if (service.title.toLowerCase().includes('property') || service.title.toLowerCase().includes('land')) {
                  ServiceIcon = Building;
                } else if (service.title.toLowerCase().includes('vehicle') || service.title.toLowerCase().includes('driver')) {
                  ServiceIcon = Truck;
                }
                // Generate a description based on the service title
                let description = `Apply for ${service.title.toLowerCase()} services online.`;
                if (service.title.toLowerCase().includes('registration')) {
                  description = `Complete your ${service.title.toLowerCase()} process online.`;
                } else if (service.title.toLowerCase().includes('license')) {
                  description = `Apply for or renew your ${service.title.toLowerCase()}.`;
                } else if (service.title.toLowerCase().includes('certificate')) {
                  description = `Request your official ${service.title.toLowerCase()}.`;
                } else if (service.title.toLowerCase().includes('permit')) {
                  description = `Apply for necessary ${service.title.toLowerCase()}.`;
                }
                return <button key={service.id} className="flex items-start p-4 border rounded-lg hover:bg-blue-50 transition-colors text-left" onClick={() => navigateToService(service.title, service.link)} data-id="element-209">
                          <div className="p-2 bg-blue-50 rounded-full mr-3 flex-shrink-0" data-id="element-210">
                            <ServiceIcon className="h-5 w-5 text-blue-600" data-id="element-211" />
                          </div>
                          <div data-id="element-212">
                            <span className="text-blue-700 font-medium block" data-id="element-213">
                              {service.title}
                            </span>
                            <span className="text-gray-500 text-sm mt-1 block" data-id="element-214">
                              {description}
                            </span>
                          </div>
                        </button>;
              })}
                  </div>
                </div>)}
              <div className="mt-6 flex justify-end" data-id="element-215">
                <button onClick={handleCloseModal} className="px-8 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition-colors" data-id="element-216">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* All Categories/Ministries Modal */}
      {showAllCategoriesModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-217">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" data-id="element-218">
            <div className="p-6" data-id="element-219">
              <div className="flex justify-between items-center mb-6" data-id="element-220">
                <h2 className="text-2xl font-bold" data-id="element-221">
                  All {viewType === 'categories' ? 'Categories' : 'Ministries'}{' '}
                  and Services
                </h2>
                <button onClick={handleCloseAllCategoriesModal} className="text-gray-500 hover:text-gray-700" data-id="element-222">
                  <X className="h-6 w-6" data-id="element-223" />
                </button>
              </div>
              {/* Search input */}
              <div className="relative mb-6" data-id="element-224">
                <input type="text" placeholder="Search services by name, category or description..." className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              trackEvent('search_services', {
                searchTerm: e.target.value,
                viewType,
                timestamp: new Date().toISOString()
              });
            }} data-id="element-225" />
                <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" data-id="element-226" />
                {searchTerm && <button className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600" onClick={() => setSearchTerm('')} data-id="element-227">
                    <X className="h-5 w-5" data-id="element-228" />
                  </button>}
              </div>
              {/* Filter pills */}
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-229">
                {viewType === 'categories' ?
            // Show category filters when in categories view
            ['Citizen', 'Business', 'Immigration', 'Document', 'Transport', 'Civil', 'Taxation', 'Property', 'Security'].map(filter => <button key={filter} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedFilter === filter ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`} onClick={() => {
              if (selectedFilter === filter) {
                setSelectedFilter(null);
              } else {
                setSelectedFilter(filter);
                trackEvent('filter_selected', {
                  filter,
                  viewType,
                  timestamp: new Date().toISOString()
                });
              }
            }} data-id="element-230">
                        {filter}
                      </button>) :
            // Show ministry filters when in agencies view
            agencies.map(agency => <button key={agency.id} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedFilter === agency.title ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`} onClick={() => {
              if (selectedFilter === agency.title) {
                setSelectedFilter(null);
              } else {
                setSelectedFilter(agency.title);
                trackEvent('filter_selected', {
                  filter: agency.title,
                  viewType,
                  timestamp: new Date().toISOString()
                });
              }
            }} data-id="element-231">
                        {agency.title.replace('Ministry of ', '')}
                      </button>)}
                {(searchTerm || selectedFilter) && <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-300" onClick={() => {
              setSearchTerm('');
              setSelectedFilter(null);
              trackEvent('clear_filters', {
                viewType,
                timestamp: new Date().toISOString()
              });
            }} data-id="element-232">
                    Clear filters
                  </button>}
              </div>
              <div className="space-y-8" data-id="element-233">
                {displayData.filter(item => {
              // Filter by selected category if any
              if (selectedFilter) {
                const matchesCategory = item.title.includes(selectedFilter);
                const hasMatchingServices = item.subcategories?.some(subcat => subcat.services.some(service => service.title.includes(selectedFilter)));
                if (!matchesCategory && !hasMatchingServices) {
                  return false;
                }
              }
              // Filter by search term if any
              if (searchTerm) {
                const matchesTitle = item.title.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesDescription = item.description.toLowerCase().includes(searchTerm.toLowerCase());
                const hasMatchingSubcategories = item.subcategories?.some(subcat => subcat.title.toLowerCase().includes(searchTerm.toLowerCase()) || subcat.services.some(service => service.title.toLowerCase().includes(searchTerm.toLowerCase())));
                return matchesTitle || matchesDescription || hasMatchingSubcategories;
              }
              return true;
            }).map(item => <div key={item.id} className="border-b pb-6 last:border-b-0" data-id="element-234">
                      <div className="flex items-center mb-4" data-id="element-235">
                        <div className="p-2 bg-blue-50 rounded-full mr-3" data-id="element-236">
                          {item.icon}
                        </div>
                        <h3 className="text-xl font-semibold" data-id="element-237">{item.title}</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pl-4" data-id="element-238">
                        {item.subcategories?.filter(subcategory => {
                  if (searchTerm) {
                    const matchesTitle = subcategory.title.toLowerCase().includes(searchTerm.toLowerCase());
                    const hasMatchingServices = subcategory.services.some(service => service.title.toLowerCase().includes(searchTerm.toLowerCase()));
                    return matchesTitle || hasMatchingServices;
                  }
                  return true;
                }).map(subcategory => <div key={subcategory.id} data-id="element-239">
                              <h4 className="font-medium text-lg mb-2 text-blue-800" data-id="element-240">
                                {subcategory.title}
                              </h4>
                              <ul className="space-y-1 ml-2" data-id="element-241">
                                {subcategory.services.filter(service => !searchTerm || service.title.toLowerCase().includes(searchTerm.toLowerCase())).map(service => <li key={service.id} data-id="element-242">
                                      <Link to={service.link} className="text-blue-600 hover:underline block py-1" onClick={() => {
                        trackEvent('service_selected_from_all', {
                          service: service.title,
                          category: item.title,
                          subcategory: subcategory.title,
                          searchTerm,
                          selectedFilter,
                          timestamp: new Date().toISOString()
                        });
                        setShowAllCategoriesModal(false);
                      }} data-id="element-243">
                                        {service.title}
                                      </Link>
                                    </li>)}
                              </ul>
                            </div>)}
                      </div>
                    </div>)}
              </div>
              {displayData.filter(item => {
            if (selectedFilter) {
              const matchesCategory = item.title.includes(selectedFilter);
              const hasMatchingServices = item.subcategories?.some(subcat => subcat.services.some(service => service.title.includes(selectedFilter)));
              if (!matchesCategory && !hasMatchingServices) {
                return false;
              }
            }
            if (searchTerm) {
              const matchesTitle = item.title.toLowerCase().includes(searchTerm.toLowerCase());
              const matchesDescription = item.description.toLowerCase().includes(searchTerm.toLowerCase());
              const hasMatchingSubcategories = item.subcategories?.some(subcat => subcat.title.toLowerCase().includes(searchTerm.toLowerCase()) || subcat.services.some(service => service.title.toLowerCase().includes(searchTerm.toLowerCase())));
              return matchesTitle || matchesDescription || hasMatchingSubcategories;
            }
            return true;
          }).length === 0 && <div className="text-center py-8" data-id="element-244">
                  <p className="text-gray-500" data-id="element-245">
                    No services found matching "{searchTerm || selectedFilter}"
                  </p>
                  <button onClick={() => {
              setSearchTerm('');
              setSelectedFilter(null);
            }} className="mt-2 text-blue-600 hover:text-blue-800" data-id="element-246">
                    Clear filters
                  </button>
                </div>}
              <div className="mt-6 flex justify-end" data-id="element-247">
                <button onClick={handleCloseAllCategoriesModal} className="px-8 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition-colors" data-id="element-248">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </section>;
};